import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Users, TrendingUp, TrendingDown, IndianRupee, Target, Calendar,
  Plus, UserPlus, CreditCard, ClipboardCheck, Video, Bell, Receipt,
  BookOpen, Clock, Phone, CheckCircle, AlertCircle, Activity,
  ArrowRight, Briefcase, GraduationCap, ChartBar, Eye, Megaphone, Loader2,
  XCircle, RefreshCw, Lock, ShieldAlert
} from 'lucide-react';
import AddLeadDialog from '@/components/center/AddLeadDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Center Status Constants (must match backend)
const CENTER_STATUS = {
  UNDER_REVIEW: 'UNDER_REVIEW',
  APPROVED_PENDING_PAYMENT: 'APPROVED_PENDING_PAYMENT',
  ACTIVE: 'ACTIVE',
  REJECTED: 'REJECTED'
};

const CenterDashboardHome = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    activeStudents: 0,
    dropouts: 0,
    todayCollection: 0,
    monthlyRevenue: 0,
    targetAchieved: 0,
    avgAttendance: 0
  });
  const [recentStudents, setRecentStudents] = useState([]);
  const [todayTasks, setTodayTasks] = useState([]);
  const [pendingFees, setPendingFees] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [centerInfo, setCenterInfo] = useState(null);
  const [dashboardStatus, setDashboardStatus] = useState('loading'); // UNDER_REVIEW, APPROVED_PENDING_PAYMENT, ACTIVE, REJECTED, loading
  const [rejectionReason, setRejectionReason] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [reapplyLoading, setReapplyLoading] = useState(false);
  
  // Quick Add Dialogs
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const navigate = useNavigate();
  
  // Lead added callback - refresh dashboard data
  const handleLeadAdded = () => {
    fetchDashboardData();
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('center_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    const headers = getAuthHeaders();
    
    try {
      // Fetch center info
      const centerRes = await axios.get(`${API}/center/me`, { headers }).catch(() => ({ data: null }));
      const centerData = centerRes.data;
      setCenterInfo(centerData);
      
      // Determine dashboard status based on center info (using new status constants)
      if (centerData) {
        const status = (centerData.status || '').toUpperCase();
        const dashboardUnlocked = centerData.dashboard_unlocked;
        const showPayment = centerData.show_payment;
        const isActive = centerData.is_active;
        
        // Check if fully ACTIVE
        if (dashboardUnlocked === true || status === 'ACTIVE' || (isActive === true && status !== 'REJECTED')) {
          setDashboardStatus(CENTER_STATUS.ACTIVE);
        } 
        // Check if REJECTED
        else if (status === 'REJECTED') {
          setDashboardStatus(CENTER_STATUS.REJECTED);
          setRejectionReason(centerData.rejection_reason || 'Your application was not approved. Please contact support for more details.');
          setLoading(false);
          return; // Don't fetch more data for rejected centers
        }
        // Check if APPROVED_PENDING_PAYMENT (needs to pay)
        else if (showPayment === true || status === 'APPROVED_PENDING_PAYMENT' || status === 'STAGE1_APPROVED' || status === 'PENDING_PAYMENT') {
          setDashboardStatus(CENTER_STATUS.APPROVED_PENDING_PAYMENT);
          setLoading(false);
          return; // Don't fetch more data for non-active centers
        } 
        // Check if UNDER_REVIEW (pending approval)
        else if (status === 'UNDER_REVIEW' || status === 'PENDING' || centerData.is_applicant === true || !status) {
          setDashboardStatus(CENTER_STATUS.UNDER_REVIEW);
          setLoading(false);
          return; // Don't fetch more data for pending centers
        } 
        // Default to active for existing centers with unknown status
        else {
          setDashboardStatus(CENTER_STATUS.ACTIVE);
        }
      }

      // Fetch CRM stats
      const crmRes = await axios.get(`${API}/center/crm/stats`, { headers }).catch(() => ({ data: {} }));
      
      // Fetch students
      const studentsRes = await axios.get(`${API}/center/students`, { headers }).catch(() => ({ data: [] }));
      const students = studentsRes.data || [];
      
      // Fetch tasks
      const tasksRes = await axios.get(`${API}/center/tasks`, { headers }).catch(() => ({ data: [] }));
      
      // Fetch fee collections
      const feesRes = await axios.get(`${API}/center/fee-collections`, { headers }).catch(() => ({ data: [] }));
      const fees = feesRes.data || [];
      
      // Calculate stats
      const activeStudents = students.filter(s => s.status === 'active').length;
      const todayFees = fees.filter(f => {
        const today = new Date().toDateString();
        return f.payment_date && new Date(f.payment_date).toDateString() === today;
      });
      const todayTotal = todayFees.reduce((sum, f) => sum + (f.amount || 0), 0);
      const monthlyTotal = fees.reduce((sum, f) => sum + (f.amount || 0), 0);

      setStats({
        activeStudents: activeStudents || students.length,
        dropouts: students.filter(s => s.status === 'dropout').length,
        todayCollection: todayTotal,
        monthlyRevenue: monthlyTotal,
        targetAchieved: 75,
        avgAttendance: 82
      });

      setRecentStudents(students.slice(0, 5));
      setTodayTasks(tasksRes.data?.slice(0, 5) || []);
      setPendingFees(fees.filter(f => f.status === 'pending').slice(0, 5));

      // Only show activity if there's real data
      setRecentActivity([]);

    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Pay Affiliation Fees via Razorpay
  const handlePayAffiliationFees = async () => {
    setPaymentLoading(true);
    const headers = getAuthHeaders();
    
    try {
      // Create Razorpay order for affiliation fee
      const fee = centerInfo?.fee || 50000;
      const orderRes = await axios.post(`${API}/payment/create-partner-order`, {
        amount: fee,
        application_id: centerInfo?.id || centerInfo?.application_id
      }, { headers });
      
      const { order_id, amount, currency, key_id } = orderRes.data;
      
      // Initialize Razorpay
      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        name: "Iqra Skills",
        description: "Partner Affiliation Fee",
        order_id: order_id,
        handler: async function (response) {
          try {
            // Verify payment
            await axios.post(`${API}/payment/verify-partner-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              application_id: centerInfo?.id || centerInfo?.application_id
            }, { headers });
            
            toast.success('Payment successful! Your dashboard is now active.');
            // Refresh page to update status
            window.location.reload();
          } catch (err) {
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: centerInfo?.owner_name || centerInfo?.name,
          email: centerInfo?.email,
          contact: centerInfo?.mobile
        },
        theme: {
          color: "#10b981"
        }
      };
      
      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    } finally {
      setPaymentLoading(false);
    }
  };
  
  // Handle Reapply for rejected applications
  const handleReapply = async () => {
    setReapplyLoading(true);
    const headers = getAuthHeaders();
    
    try {
      const applicationId = centerInfo?.id || centerInfo?.application_id;
      await axios.post(`${API}/partner/reapply/${applicationId}`, {}, { headers });
      
      toast.success('Your application has been resubmitted for review!');
      // Refresh to show updated status
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reapply. Please contact support.');
    } finally {
      setReapplyLoading(false);
    }
  };

  const quickActions = [
    { icon: UserPlus, label: 'Add Student', color: 'from-blue-500 to-blue-600', path: '/center/students' },
    { icon: Megaphone, label: 'Add Lead', color: 'from-purple-500 to-purple-600', action: () => setAddLeadOpen(true) },
    { icon: CreditCard, label: 'Collect Fees', color: 'from-emerald-500 to-emerald-600', path: '/center/finance' },
    { icon: ClipboardCheck, label: 'Attendance', color: 'from-amber-500 to-amber-600', path: '/center/attendance' },
    { icon: BookOpen, label: 'Create Batch', color: 'from-cyan-500 to-cyan-600', path: '/center/batches' },
    { icon: Video, label: 'Start Live Class', color: 'from-pink-500 to-pink-600', path: '/center/lms' },
    { icon: Bell, label: 'Send Notification', color: 'from-orange-500 to-orange-600', path: '/center/students' },
    { icon: Receipt, label: 'Generate Receipt', color: 'from-indigo-500 to-indigo-600', path: '/center/finance' },
  ];
  
  // Lead management is now handled by unified AddLeadDialog component

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
      </div>
    );
  }

  // Show UNDER_REVIEW Status - Application Pending
  if (dashboardStatus === CENTER_STATUS.UNDER_REVIEW) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="center-under-review-status">
        <div className="text-center max-w-md mx-auto p-8 bg-white rounded-2xl shadow-lg border border-amber-100">
          <div className="w-20 h-20 mx-auto mb-6 bg-amber-100 rounded-full flex items-center justify-center">
            <Clock className="w-10 h-10 text-amber-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-3">Application Under Review</h2>
          <p className="text-slate-600 mb-4">
            Your partner application is currently being reviewed by our team. 
            You will receive an email notification once your application is approved.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
              <p className="text-sm font-semibold text-amber-800">Status: Under Review</p>
            </div>
            <p className="text-sm text-amber-600">
              Application ID: {centerInfo?.id?.slice(0, 8) || centerInfo?.application_id?.slice(0, 8) || 'N/A'}
            </p>
          </div>
          
          {/* Locked Features Info */}
          <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left">
            <div className="flex items-center gap-2 mb-3">
              <Lock className="w-4 h-4 text-slate-500" />
              <p className="text-sm font-medium text-slate-700">Features Locked Until Approval</p>
            </div>
            <ul className="text-xs text-slate-500 space-y-1">
              <li>• Student Management</li>
              <li>• Fee Collection</li>
              <li>• CRM & Lead Management</li>
              <li>• Reports & Analytics</li>
            </ul>
          </div>
          
          <div className="text-sm text-slate-500">
            <p>Need help? Contact us at:</p>
            <p className="font-medium text-slate-700">support@iqraskills.org</p>
          </div>
        </div>
      </div>
    );
  }

  // Show APPROVED_PENDING_PAYMENT - Pay Affiliation Fees
  if (dashboardStatus === CENTER_STATUS.APPROVED_PENDING_PAYMENT) {
    const partnerFees = {
      'SP': 500000,
      'DLC': 150000, 
      'TC': 50000
    };
    const fee = centerInfo?.fee || partnerFees[centerInfo?.partner_type] || 50000;
    
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="center-payment-pending-status">
        <div className="text-center max-w-lg mx-auto p-8 bg-white rounded-2xl shadow-lg border border-emerald-100">
          <div className="w-20 h-20 mx-auto mb-6 bg-emerald-100 rounded-full flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-3">Application Approved! 🎉</h2>
          <p className="text-slate-600 mb-6">
            Congratulations! Your application has been approved. 
            Complete your affiliation fee payment to activate your center dashboard.
          </p>
          
          <div className="bg-slate-50 rounded-xl p-6 mb-6">
            <div className="text-sm text-slate-500 mb-1">Affiliation Fee</div>
            <div className="text-4xl font-bold text-emerald-600 mb-2">
              ₹{fee.toLocaleString()}
            </div>
            <div className="text-xs text-slate-500">
              Partner Type: {centerInfo?.partner_type === 'SP' ? 'State Partner' : 
                            centerInfo?.partner_type === 'DLC' ? 'District Level Center' : 'Training Center'}
            </div>
          </div>
          
          {/* What you get after payment */}
          <div className="bg-emerald-50 rounded-lg p-4 mb-6 text-left border border-emerald-200">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle className="w-4 h-4 text-emerald-600" />
              <p className="text-sm font-medium text-emerald-700">After Payment You'll Get:</p>
            </div>
            <ul className="text-xs text-emerald-600 space-y-1">
              <li>✓ Full Dashboard Access</li>
              <li>✓ Student Management System</li>
              <li>✓ Fee Collection & Revenue Tracking</li>
              <li>✓ CRM & Lead Management</li>
              <li>✓ Reports & Analytics</li>
            </ul>
          </div>
          
          <Button 
            onClick={handlePayAffiliationFees}
            disabled={paymentLoading}
            size="lg"
            className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold py-6 text-lg"
            data-testid="pay-affiliation-fees-btn"
          >
            {paymentLoading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CreditCard className="w-5 h-5 mr-2" />
                Pay Affiliation Fees
              </>
            )}
          </Button>
          
          <p className="text-xs text-slate-500 mt-4">
            Secure payment powered by Razorpay
          </p>
        </div>
      </div>
    );
  }
  
  // Show REJECTED Status - Application Rejected
  if (dashboardStatus === CENTER_STATUS.REJECTED) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="center-rejected-status">
        <div className="text-center max-w-lg mx-auto p-8 bg-white rounded-2xl shadow-lg border border-red-100">
          <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
            <XCircle className="w-10 h-10 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-3">Application Not Approved</h2>
          <p className="text-slate-600 mb-4">
            Unfortunately, your partner application was not approved at this time.
          </p>
          
          {/* Rejection Reason */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-left">
            <div className="flex items-center gap-2 mb-2">
              <ShieldAlert className="w-4 h-4 text-red-600" />
              <p className="text-sm font-semibold text-red-800">Reason for Rejection:</p>
            </div>
            <p className="text-sm text-red-700">
              {rejectionReason || 'No specific reason provided. Please contact support for more details.'}
            </p>
          </div>
          
          {/* Reapply Option */}
          <div className="bg-slate-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-slate-600 mb-3">
              You can reapply after addressing the concerns mentioned above.
            </p>
            <Button 
              onClick={handleReapply}
              disabled={reapplyLoading}
              variant="outline"
              className="w-full border-purple-300 text-purple-700 hover:bg-purple-50"
              data-testid="reapply-btn"
            >
              {reapplyLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Re-Apply for Partnership
                </>
              )}
            </Button>
          </div>
          
          <div className="text-sm text-slate-500">
            <p>Need clarification? Contact us at:</p>
            <p className="font-medium text-slate-700">support@iqraskills.org</p>
            <p className="text-xs text-slate-400 mt-1">Application ID: {centerInfo?.id?.slice(0, 8) || 'N/A'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="center-dashboard-home">
      {/* Welcome Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Welcome back, {centerInfo?.owner_name?.split(' ')[0] || 'Partner'} 👋
          </h1>
          <p className="text-slate-500 mt-1">Here's what's happening at your center today</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setAddLeadOpen(true)} variant="outline" size="sm" className="gap-2 border-purple-300 text-purple-700 hover:bg-purple-50">
            <Megaphone className="w-4 h-4" />
            Add Lead
          </Button>
          <Button onClick={() => navigate('/center/students')} size="sm" className="gap-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700">
            <UserPlus className="w-4 h-4" />
            Add Student
          </Button>
          <Button onClick={() => navigate('/center/finance')} size="sm" className="gap-2 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700">
            <CreditCard className="w-4 h-4" />
            Collect Fees
          </Button>
          <Button onClick={fetchDashboardData} variant="outline" size="sm" className="gap-2">
            <Activity className="w-4 h-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Smart Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {/* Active Students */}
        <motion.div 
          whileHover={{ scale: 1.02, y: -2 }}
          className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-5 text-white shadow-lg shadow-blue-500/20"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-blue-100 text-xs font-medium">Active Students</p>
              <p className="text-3xl font-bold mt-1">{stats.activeStudents}</p>
              <div className="flex items-center gap-1 mt-2">
                <TrendingUp className="w-3 h-3" />
                <span className="text-xs">+12% growth</span>
              </div>
            </div>
            <Users className="w-8 h-8 text-blue-200" />
          </div>
        </motion.div>

        {/* Dropouts */}
        <motion.div 
          whileHover={{ scale: 1.02, y: -2 }}
          className="bg-gradient-to-br from-red-500 to-red-600 rounded-2xl p-5 text-white shadow-lg shadow-red-500/20"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-red-100 text-xs font-medium">Dropouts</p>
              <p className="text-3xl font-bold mt-1">{stats.dropouts}</p>
              <div className="flex items-center gap-1 mt-2">
                <AlertCircle className="w-3 h-3" />
                <span className="text-xs">Needs attention</span>
              </div>
            </div>
            <TrendingDown className="w-8 h-8 text-red-200" />
          </div>
        </motion.div>

        {/* Today's Collection */}
        <motion.div 
          whileHover={{ scale: 1.02, y: -2 }}
          className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-5 text-white shadow-lg shadow-emerald-500/20"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-emerald-100 text-xs font-medium">Today Collection</p>
              <p className="text-3xl font-bold mt-1">₹{stats.todayCollection.toLocaleString()}</p>
              <div className="flex items-center gap-1 mt-2">
                <CheckCircle className="w-3 h-3" />
                <span className="text-xs">On track</span>
              </div>
            </div>
            <IndianRupee className="w-8 h-8 text-emerald-200" />
          </div>
        </motion.div>

        {/* Monthly Revenue */}
        <motion.div 
          whileHover={{ scale: 1.02, y: -2 }}
          className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-5 text-white shadow-lg shadow-purple-500/20"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-purple-100 text-xs font-medium">Monthly Revenue</p>
              <p className="text-3xl font-bold mt-1">₹{(stats.monthlyRevenue / 1000).toFixed(0)}K</p>
              <div className="flex items-center gap-1 mt-2">
                <TrendingUp className="w-3 h-3" />
                <span className="text-xs">+18% vs last</span>
              </div>
            </div>
            <ChartBar className="w-8 h-8 text-purple-200" />
          </div>
        </motion.div>

        {/* Target Achieved */}
        <motion.div 
          whileHover={{ scale: 1.02, y: -2 }}
          className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl p-5 text-white shadow-lg shadow-amber-500/20"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-amber-100 text-xs font-medium">Target Achieved</p>
              <p className="text-3xl font-bold mt-1">{stats.targetAchieved}%</p>
              <div className="w-full h-1.5 bg-white/30 rounded-full mt-2 overflow-hidden">
                <div 
                  className="h-full bg-white rounded-full transition-all"
                  style={{ width: `${stats.targetAchieved}%` }}
                />
              </div>
            </div>
            <Target className="w-8 h-8 text-amber-200" />
          </div>
        </motion.div>

        {/* Attendance Avg */}
        <motion.div 
          whileHover={{ scale: 1.02, y: -2 }}
          className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-2xl p-5 text-white shadow-lg shadow-cyan-500/20"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-cyan-100 text-xs font-medium">Attendance Avg</p>
              <p className="text-3xl font-bold mt-1">{stats.avgAttendance}%</p>
              <div className="flex items-center gap-1 mt-2">
                <CheckCircle className="w-3 h-3" />
                <span className="text-xs">Good</span>
              </div>
            </div>
            <Calendar className="w-8 h-8 text-cyan-200" />
          </div>
        </motion.div>
      </div>

      {/* Quick Actions Hub */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <span className="text-2xl">⚡</span> Quick Actions
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          {quickActions.map((action, idx) => {
            const Icon = action.icon;
            const content = (
              <motion.div
                whileHover={{ scale: 1.05, y: -3 }}
                whileTap={{ scale: 0.98 }}
                className={`bg-gradient-to-br ${action.color} rounded-xl p-4 text-white text-center cursor-pointer shadow-lg hover:shadow-xl transition-shadow`}
              >
                <Icon className="w-6 h-6 mx-auto mb-2" />
                <p className="text-xs font-medium">{action.label}</p>
              </motion.div>
            );
            
            if (action.action) {
              return (
                <div key={idx} onClick={action.action}>
                  {content}
                </div>
              );
            }
            return (
              <Link key={idx} to={action.path}>
                {content}
              </Link>
            );
          })}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Finance Panel - Simplified */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <IndianRupee className="w-5 h-5 text-emerald-500" />
            Finance Overview
          </h3>
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-xl p-4 border border-emerald-200">
              <p className="text-sm text-emerald-600">Total Revenue</p>
              <p className="text-2xl font-bold text-emerald-700">₹{stats.monthlyRevenue.toLocaleString()}</p>
            </div>
            
            <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
              <p className="text-sm text-purple-600">Center Share</p>
              <p className="text-2xl font-bold text-purple-700">₹{Math.round(stats.monthlyRevenue * 0.6).toLocaleString()}</p>
            </div>
            
            <div className="bg-gradient-to-r from-amber-50 to-amber-100 rounded-xl p-4 border border-amber-200">
              <p className="text-sm text-amber-600">Pending Fees</p>
              <p className="text-2xl font-bold text-amber-700">₹{Math.round(stats.monthlyRevenue * 0.2).toLocaleString()}</p>
            </div>
            
            <Link to="/center/finance">
              <Button className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700">
                View Finance Details
              </Button>
            </Link>
          </div>
        </div>

        {/* Tasks Panel */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-blue-500" />
            Today's Tasks
          </h3>
          <div className="space-y-3">
            {todayTasks.length === 0 ? (
              <div className="text-center py-6">
                <CheckCircle className="w-12 h-12 text-emerald-200 mx-auto mb-2" />
                <p className="text-slate-500 text-sm">All tasks completed!</p>
              </div>
            ) : (
              todayTasks.map((task, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                  <div className={`w-2 h-2 rounded-full ${
                    task.priority === 'high' ? 'bg-red-500' :
                    task.priority === 'medium' ? 'bg-amber-500' : 'bg-blue-500'
                  }`} />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900">{task.title || 'Task'}</p>
                    <p className="text-xs text-slate-500">{task.due_time || 'Today'}</p>
                  </div>
                  <button className="text-slate-400 hover:text-emerald-500">
                    <CheckCircle className="w-5 h-5" />
                  </button>
                </div>
              ))
            )}
            {/* Empty state for tasks */}
            {todayTasks.length === 0 && (
              <div className="text-center py-6 text-slate-500">
                <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No pending tasks</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-purple-500" />
            Recent Activity
          </h3>
          <div className="space-y-3">
            {recentActivity.length > 0 ? recentActivity.map((activity) => (
              <div key={activity.id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  activity.type === 'fee' ? 'bg-emerald-100' :
                  activity.type === 'student' ? 'bg-blue-100' :
                  activity.type === 'lead' ? 'bg-purple-100' : 'bg-amber-100'
                }`}>
                  {activity.type === 'fee' ? <IndianRupee className="w-4 h-4 text-emerald-600" /> :
                   activity.type === 'student' ? <GraduationCap className="w-4 h-4 text-blue-600" /> :
                   activity.type === 'lead' ? <Briefcase className="w-4 h-4 text-purple-600" /> :
                   <Phone className="w-4 h-4 text-amber-600" />}
                </div>
                <div className="flex-1">
                  <p className="text-sm text-slate-700">{activity.message}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{activity.time}</p>
                </div>
              </div>
            )) : (
              <div className="text-center py-6 text-slate-500">
                <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No recent activity</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Students Snapshot Table */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-500" />
            Student Snapshot
          </h3>
          <Link to="/center/students">
            <Button variant="outline" size="sm" className="gap-2">
              View All <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
        {/* Responsive Table Container */}
        <div 
          className="overflow-x-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100 hover:scrollbar-thumb-slate-400"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#cbd5e1 #f1f5f9'
          }}
          data-testid="student-snapshot-table-container"
        >
          <table className="w-full min-w-[800px]">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500 whitespace-nowrap min-w-[180px]">Name</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500 whitespace-nowrap min-w-[120px]">Course</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500 whitespace-nowrap min-w-[80px]">Fees</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500 whitespace-nowrap min-w-[140px]">Attendance</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500 whitespace-nowrap min-w-[100px]">Status</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500 whitespace-nowrap min-w-[100px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {recentStudents.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-slate-500">
                    No students yet. Add your first student!
                  </td>
                </tr>
              ) : (
                recentStudents.map((student, idx) => (
                  <tr key={idx} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="py-3 px-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-blue-600 font-medium text-sm">{student.name?.charAt(0)}</span>
                        </div>
                        <span className="font-medium text-slate-900 truncate max-w-[120px]">{student.name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-slate-600 whitespace-nowrap truncate max-w-[100px]">{student.course || 'N/A'}</td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        student.fee_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {student.fee_status === 'paid' ? 'Paid' : 'Due'}
                      </span>
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 bg-slate-100 rounded-full overflow-hidden flex-shrink-0">
                          <div 
                            className={`h-full rounded-full ${
                              (student.attendance || 80) >= 75 ? 'bg-emerald-500' : 'bg-amber-500'
                            }`}
                            style={{ width: `${student.attendance || 80}%` }}
                          />
                        </div>
                        <span className="text-sm text-slate-600">{student.attendance || 80}%</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        student.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                        student.status === 'warning' ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {student.status || 'Active'}
                      </span>
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button className="p-1.5 hover:bg-slate-100 rounded-lg">
                          <Eye className="w-4 h-4 text-slate-400" />
                        </button>
                        <button className="p-1.5 hover:bg-slate-100 rounded-lg">
                          <Phone className="w-4 h-4 text-slate-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
              {/* Empty state if no students */}
              {recentStudents.length === 0 && (
                <tr>
                  <td colSpan="6" className="py-12 text-center">
                    <div className="text-slate-400">
                      <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">No students enrolled yet</p>
                      <p className="text-xs mt-1">Add your first student to get started</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Mobile Scroll Hint */}
        <div className="lg:hidden px-4 py-2 text-center text-xs text-slate-400 border-t border-slate-100 mt-2">
          ← Scroll horizontally to view all columns →
        </div>
      </div>
      
      {/* Unified Add Lead Dialog - Connected to CRM system */}
      <AddLeadDialog
        open={addLeadOpen}
        onOpenChange={setAddLeadOpen}
        onSuccess={handleLeadAdded}
      />
    </div>
  );
};

export default CenterDashboardHome;
